/**
 * Utilitats compartides per messaging (frontend)
 * - interpolateTemplate: renderitza plantilles amb placeholders {variable}
 * - messageKindLabels: etiquetes human-friendly per kind
 * - messageKindEmojis: emojis per kind
 * - sampleVariables: valors de mostra per preview
 * - ACCESS_MESSAGE_PROPERTY_IDS: propietats amb missatge de codi (tanda «dia a dia», D6)
 * - phoneE164: telèfon per trucar (`tel:`), del backend o, si no el porta, del número en brut
 */

import type {
  ComposeResult,
  CreateMessagingTaskRequest,
  CreateMessagingTaskResponse,
  MessageKind,
} from '@guilleumes/types';

/** Substitueix {variable} per valors en una plantilla */
export function interpolateTemplate(
  template: string,
  variables: Record<string, string>
): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

/** Detecta tots els placeholders {variable} d'una plantilla */
export function extractPlaceholders(template: string): string[] {
  const matches = template.match(/\{(\w+)\}/g);
  if (!matches) return [];
  return [...new Set(matches.map(m => m.slice(1, -1)))]; // Elimina { i }
}

/** Etiquetes per kind (per UI) */
export const messageKindLabels: Record<MessageKind, string> = {
  FIRST_CONTACT: 'Primer contacte',
  WELCOME: 'Benvinguda',
  GUEST_FORM: 'Registre',
  ACCESS: 'Accés',
  PAYMENT_LINK: 'Pagament',
  REVIEW: 'Review',
  CUSTOM: 'Personalitzat',
};

/** Emojis per kind (per UI) */
export const messageKindEmojis: Record<MessageKind, string> = {
  FIRST_CONTACT: '📩',
  WELCOME: '👋',
  GUEST_FORM: '📋',
  ACCESS: '🔑',
  PAYMENT_LINK: '💳',
  REVIEW: '⭐',
  CUSTOM: '✏️',
};

/** Ordre de kinds per UI (matriu admin) */
export const MESSAGE_KINDS_ORDER: MessageKind[] = [
  'FIRST_CONTACT',
  'WELCOME',
  'GUEST_FORM',
  'ACCESS',
  'PAYMENT_LINK',
  'REVIEW',
  'CUSTOM',
];

/** Idiomes suportats */
export const SUPPORTED_LANGUAGES = ['ca', 'es', 'en', 'fr'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/** Etiquetes per idioma */
export const languageLabels: Record<SupportedLanguage, string> = {
  ca: 'Català',
  es: 'Castellà',
  en: 'Anglès',
  fr: 'Francès',
};

/** Valors de mostra per preview (admin) */
export const sampleVariables: Record<string, string> = {
  nom: 'Frederic',
  cognom: 'Garcia',
  hab: 'Cami 3',
  telefon: '+34 600 123 456',
  codi: '9288',
  staff: 'Pere',
  hotel: 'Hotel Guilleumes Montserrat',
  wifi: 'guilleumes1980',
  checkin: '15/04/2026',
  checkout: '20/04/2026',
  nits: '5',
  preu: '450',
  // Tanda «dia a dia» (§4.2): les variables noves de WELCOME i ACCESS. Exemples en català,
  // perquè la vista prèvia de `/admin/messaging-templates` no ensenyi claus buides.
  remitent: 'la Carme',
  habNum: '101',
  planta: 'planta baixa',
  estada: '2 nits',
  diaEntrada: 'dilluns',
  dataEntrada: '29 de setembre',
  diaSortida: 'dimecres',
  dataSortida: "1 d'octubre",
  llits: 'Habitació amb un llit de matrimoni.',
  // Registre (F6): l'enllaç del formulari de viatgers; el de debò el crea el backend.
  formulari: 'https://pms.example/formulari/abc123',
};

/**
 * Genera variables de mostra per preview (merge amb defaults).
 *
 * Un valor que és la mateixa clau entre claus (`{ nom: '{nom}' }`, el que passa l'editor de
 * plantilles per a cada variable definida) no tapa l'exemple: si no, la vista prèvia ensenyaria
 * `{nom}` just per a les variables que la plantilla fa servir.
 */
export function generateSampleVariables(
  customVars?: Record<string, string>
): Record<string, string> {
  const merged: Record<string, string> = { ...sampleVariables };
  for (const [key, value] of Object.entries(customVars ?? {})) {
    if (value === `{${key}}` && key in sampleVariables) continue;
    merged[key] = value;
  }
  return merged;
}

// ═══════════════════════════════════════════════════════════════
// Tanda «dia a dia» (plans/dia-a-dia-calendari-i-whatsapp.md §3 i §4.7)
// ═══════════════════════════════════════════════════════════════

/**
 * Propietats (id de Beds24) que tenen missatge de codi d'accés (`ACCESS`). Avui només l'hotel
 * (D6); els pisos encara no en tenen i el botó Codi no hi surt. Germana de la constant del
 * backend (`messaging/`); no és a `packages/types` perquè el dist local no es refà sol.
 */
export const ACCESS_MESSAGE_PROPERTY_IDS: readonly number[] = [18770];

/** La propietat té missatge de codi? Sense propietat coneguda, no. */
export function hasAccessMessage(beds24PropertyId: number | null | undefined): boolean {
  return beds24PropertyId != null && ACCESS_MESSAGE_PROPERTY_IDS.includes(beds24PropertyId);
}

/**
 * El backend no ha pogut calcular el `{codi}` del missatge ACCESS (§4.7): ho diu a `warnings`
 * («Variable requerida "codi"…», «Placeholder "codi"…» o un avís propi que parli del codi) o
 * el placeholder encara hi és. Llavors toca escriure'l a mà al `MessageComposeSheet`.
 */
export function isAccessCodeMissing(result: { text: string; warnings: string[] }): boolean {
  if (result.text.includes('{codi}')) return true;
  // Si la variable de la plantilla no és requerida, el backend no avisa: la línia queda buida.
  if (EMPTY_CODE_LINE.test(result.text)) return true;
  return result.warnings.some((w) => /\bcodi\b/i.test(w));
}

/** La línia del codi dels textos d'ACCESS dels 4 idiomes («Codi:», «Código:», «Code:», «Code :»), buida. */
const EMPTY_CODE_LINE = /^[ \t]*(?:Codi|Código|Code)[ \t]?:[ \t]*$/m;

/**
 * Posa el codi a la línia «Codi:» d'un text ja renderitzat pel backend, que l'ha deixada buida
 * perquè no l'ha pogut calcular. Només per a la vista prèvia: el text de debò el torna el backend.
 * Si no troba la línia (plantilla retocada), torna el text tal com és.
 */
export function fillAccessCodeLine(text: string, codi: string): string {
  return text.replace(/^([ \t]*(?:Codi|Código|Code)[ \t]?:)[ \t]*$/m, `$1 ${codi}`);
}

/** El missatge ACCESS amb el codi escrit a mà, a punt per obrir WhatsApp. */
export interface AccessWithCodeResult {
  /** La tasca on queda el missatge (la que hi havia, si n'hi havia): el «enviat» va aquí. */
  taskId: string;
  text: string;
  whatsappLink: string;
  phoneValid: boolean;
}

/**
 * ACCESS amb el codi escrit a mà al `MessageComposeSheet`.
 *
 * - Hi ha tasca (`existingTaskId`: el botó Codi ja l'ha creada i el backend no ha pogut calcular
 *   el codi): es **reutilitza** amb `POST /messaging/tasks/:id/compose` i `manualVariables.codi`.
 *   Abans es tornava a cridar `createTask` i quedaven dues tasques, la primera pendent per sempre.
 * - No n'hi ha (el compositor obert sense tasca prèvia): se'n crea una, com sempre.
 */
export async function prepareAccessWithCode(
  input: { reservationId: string; codi: string; existingTaskId?: string | null },
  calls: {
    createTask: (req: CreateMessagingTaskRequest) => Promise<CreateMessagingTaskResponse>;
    composeTask: (req: {
      taskId: string;
      manualVariables: Record<string, string>;
    }) => Promise<ComposeResult>;
  },
): Promise<AccessWithCodeResult> {
  const manualVariables = { codi: input.codi };
  if (input.existingTaskId) {
    const composed = await calls.composeTask({ taskId: input.existingTaskId, manualVariables });
    return {
      taskId: input.existingTaskId,
      text: composed.text,
      whatsappLink: composed.whatsappLink,
      phoneValid: composed.phoneValid,
    };
  }
  const created = await calls.createTask({
    reservationId: input.reservationId,
    messageKind: 'ACCESS',
    manualVariables,
  });
  return {
    taskId: created.taskId,
    text: created.text,
    whatsappLink: created.whatsappLink,
    phoneValid: created.phoneValid,
  };
}

/**
 * El telèfon en E.164 (`+34600111222`) per trucar (D13: a Android, `tel:` necessita el prefix).
 *
 * - `e164` definit (el calcula el backend, `guestPhoneE164`): mana, també quan és `null`
 *   (el backend ja ha dit que no és vàlid o que és un número de farciment d'una OTA).
 * - `e164` `undefined` (qui pinta no el porta: la fitxa oberta per URL, per exemple): només
 *   si el número en brut ja porta el prefix (`+` o `00`); un número sense prefix no s'endevina.
 */
export function phoneE164(
  rawPhone: string | null | undefined,
  e164: string | null | undefined,
): string | null {
  if (e164 !== undefined) return e164 || null;
  const raw = (rawPhone ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  let international: string | null = null;
  if (raw.startsWith('+')) international = digits;
  else if (raw.startsWith('00')) international = digits.slice(2);
  if (!international || international.length < 8 || international.length > 15) return null;
  return `+${international}`;
}

/** Títols dels botons desactivats de la fila Welcome · Codi · Trucar. */
export const QUICK_ACTION_NO_PHONE = 'Sense telèfon';
export const QUICK_ACTION_NO_PREFIX = 'Telèfon sense prefix internacional';

/** Què pot fer la fila Welcome · Codi · Trucar d'una reserva (§3.1). */
export interface QuickWhatsAppState {
  /** El número per a `tel:` i per saber si es pot oferir WhatsApp. */
  e164: string | null;
  /** El botó Codi surt (D6: només les propietats d'`ACCESS_MESSAGE_PROPERTY_IDS`). */
  showAccess: boolean;
  /** Per què els botons estan desactivats; `null` si funcionen. */
  disabledReason: string | null;
}

export function resolveQuickWhatsAppState(input: {
  phone?: string | null;
  phoneE164?: string | null;
  beds24PropertyId?: number | null;
}): QuickWhatsAppState {
  const e164 = phoneE164(input.phone, input.phoneE164);
  const hasRawPhone = !!input.phone?.trim();
  return {
    e164,
    showAccess: hasAccessMessage(input.beds24PropertyId),
    disabledReason: e164 ? null : hasRawPhone ? QUICK_ACTION_NO_PREFIX : QUICK_ACTION_NO_PHONE,
  };
}

// ═══════════════════════════════════════════════════════════════
// Registre (F6, `plans/dia-a-dia-calendari-i-whatsapp.md` §7bis, D14-D16)
// ═══════════════════════════════════════════════════════════════

/**
 * Claus de TanStack Query que porten els `accessTokens` d'una reserva (`GET /reservations/:id`):
 * la del popover i la fitxa (`reservation-full`) i la del modal d'edició (`reservation-tokens`).
 * El botó Registre les invalida: el backend pot haver creat un formulari nou (D15), i el «Copiar
 * link» l'ha de veure.
 */
export function guestFormTokenQueryKeys(reservationId: string): readonly (readonly unknown[])[] {
  return [
    ['reservation-full', reservationId],
    ['reservation-tokens', reservationId],
  ] as const;
}

/** Un `accessToken` tal com el torna `GET /reservations/:id` (només els camps que calen aquí). */
export interface GuestFormTokenLike {
  tokenHash?: string | null;
  tokenType?: string | null;
  status?: string | null;
  expiresAt?: string | Date | null;
}

/**
 * L'URL del formulari de la reserva per al «Copiar link»: el token individual que encara obre el
 * formulari (ni revocat ni caducat, com `validateToken` del backend), el que caduca més tard. Abans
 * es copiava el primer de la llista, que podia ser un de caducat o revocat encara que el Registre
 * n'hagués creat un de nou. Sense cap de vàlid, `null` (el botó queda desactivat).
 */
export function pickGuestFormUrl(
  tokens: readonly GuestFormTokenLike[] | null | undefined,
  /** Per defecte, `window.location.origin` (només es llegeix si hi ha un formulari vàlid). */
  origin?: string,
  now: Date = new Date(),
): string | null {
  if (!tokens?.length) return null;
  const expiry = (t: GuestFormTokenLike) => {
    const ms = t.expiresAt ? new Date(t.expiresAt).getTime() : Number.POSITIVE_INFINITY;
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
  };
  const valid = tokens
    .filter(
      (t) =>
        !!t.tokenHash &&
        (!t.tokenType || t.tokenType === 'GUEST_FORM') &&
        t.status !== 'REVOKED' &&
        expiry(t) > now.getTime(),
    )
    .sort((a, b) => expiry(b) - expiry(a));
  if (!valid[0]) return null;
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/formulari/${valid[0].tokenHash}`;
}
